/**
 * Pure functions of the `word_groups` type: the progress blob, the submission and how a
 * server verdict folds back into the progress (ADR 0059). No component here, so `module.ts`
 * stays light in the eager bundle.
 */
import type { Verdict } from "../types";
import { foldWord, sameSet } from "./groups";
import type {
  FoundGroup,
  Outcome,
  WordGroupsDefinition,
  WordGroupsProgress,
  WordGroupsSubmission,
} from "./types";

const OUTCOMES: readonly Outcome[] = ["found", "one_away", "miss", "repeat", "invalid"];

export const DEFAULT_RULES = {
  fr: "Les mots de la grille forment des groupes secrets, chacun lié par un point commun. Sélectionnez les mots d'un groupe et soumettez-les. Un groupe trouvé se dévoile avec son thème ; « À un mot près » signale qu'il ne manque qu'un mot. Les erreurs sont comptées, sans limite. Attention aux mots qui semblent aller dans deux groupes.",
  en: "The words on the board form secret groups, each sharing a link. Select the words of one group and submit them. A group you find is revealed with its theme; “One away” means a single word is wrong. Mistakes are counted, never capped. Beware of words that seem to fit two groups.",
};

export function emptyDefinition(): WordGroupsDefinition {
  return {
    puzzleType: "word_groups",
    typeVersion: 1,
    language: "fr",
    groupCount: 4,
    groupSize: 4,
    words: Array.from({ length: 16 }, () => ""),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: WordGroupsDefinition): WordGroupsProgress {
  return { version: 1, order: [], selected: [], guesses: [], outcomes: [], found: [], mistakes: 0 };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function asFound(value: unknown): FoundGroup | null {
  // The server sends `[guess, group, label]`; the progress stores objects.
  if (Array.isArray(value) && value.length === 3) {
    const [guess, group, label] = value;
    return asFound({ guess, group, label });
  }
  if (!isRecord(value)) return null;
  const { guess, group, label } = value;
  if (typeof guess !== "number" || !Number.isInteger(guess) || guess < 0) return null;
  if (typeof group !== "number" || !Number.isInteger(group) || group < 0) return null;
  if (typeof label !== "string") return null;
  return { guess, group, label };
}

function asOutcome(value: unknown): Outcome {
  return OUTCOMES.includes(value as Outcome) ? (value as Outcome) : "invalid";
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: WordGroupsDefinition,
  raw: unknown,
): WordGroupsProgress {
  if (!isRecord(raw)) return emptyProgress(definition);
  const guesses = Array.isArray(raw.guesses) ? raw.guesses.map(strings) : [];
  const outcomes = Array.isArray(raw.outcomes) ? raw.outcomes.map(asOutcome) : [];
  const found = Array.isArray(raw.found)
    ? raw.found.map(asFound).filter((f): f is FoundGroup => f !== null && f.guess < guesses.length)
    : [];
  const progress: WordGroupsProgress = {
    version: 1,
    order: strings(raw.order),
    selected: strings(raw.selected).slice(0, definition.groupSize),
    guesses,
    outcomes: guesses.map((_, index) => outcomes[index] ?? "invalid"),
    found,
    mistakes:
      typeof raw.mistakes === "number" && Number.isInteger(raw.mistakes) && raw.mistakes >= 0
        ? raw.mistakes
        : 0,
  };
  if (raw.solved === true) progress.solved = true;
  return progress;
}

/** The words of every group found so far, folded. */
export function foundWords(progress: WordGroupsProgress): Set<string> {
  const words = new Set<string>();
  for (const { guess } of progress.found) {
    for (const word of progress.guesses[guess] ?? []) words.add(foldWord(word));
  }
  return words;
}

/**
 * The tiles still on the board, in the player's arrangement: the stored order when it is a
 * permutation of the definition's words, the definition's order otherwise.
 */
export function boardWords(
  definition: WordGroupsDefinition,
  progress: WordGroupsProgress,
): string[] {
  const words = definition.words;
  const order = progress.order;
  const valid =
    order.length === words.length &&
    sameSet(order, words) &&
    new Set(order.map(foldWord)).size === order.length;
  const done = foundWords(progress);
  return (valid ? order : words).filter((word) => !done.has(foldWord(word)));
}

/** The earlier proposal of this very set, if any: the Player refuses it without a round trip. */
export function previousGuess(progress: WordGroupsProgress, words: string[]): number | null {
  const index = progress.guesses.findIndex((guess) => sameSet(guess, words));
  return index >= 0 ? index : null;
}

/** Whether the selection is a full, new proposal. */
export function canPropose(
  definition: WordGroupsDefinition,
  progress: WordGroupsProgress,
): boolean {
  return (
    !progress.solved &&
    progress.selected.length === definition.groupSize &&
    previousGuess(progress, progress.selected) === null
  );
}

/**
 * Every proposal so far plus the selection when it is a full, new one: the verdict is a
 * function of the whole list, so a teammate's proposals (shared through the progress) count.
 */
export function serializeSubmission(
  definition: WordGroupsDefinition,
  progress: WordGroupsProgress,
): WordGroupsSubmission {
  const guesses = progress.guesses.map((guess) => [...guess]);
  if (canPropose(definition, progress)) guesses.push([...progress.selected]);
  return { guesses };
}

/**
 * Folds one verdict into the progress: the server's spelling of the proposals replaces ours,
 * outcomes, found groups and mistakes are taken whole, the selection is cleared when it was
 * part of the submission. A verdict without a reading (malformed submission) changes nothing.
 */
export function applyVerdict(
  _definition: WordGroupsDefinition,
  progress: WordGroupsProgress,
  verdict: Verdict<WordGroupsSubmission>,
): WordGroupsProgress {
  const reading = verdict.feedback.find((item) => item.code === "groups");
  if (!reading) return progress;
  const params = reading.params ?? {};
  const guesses = Array.isArray(params.guesses)
    ? params.guesses.map(strings)
    : verdict.submission.guesses.map((guess) => [...guess]);
  const outcomes = Array.isArray(params.outcomes) ? params.outcomes.map(asOutcome) : [];
  const found = Array.isArray(params.found)
    ? params.found.map(asFound).filter((f): f is FoundGroup => f !== null)
    : [];
  const submitted = verdict.submission.guesses;
  const selectionSent =
    submitted.length > progress.guesses.length &&
    sameSet(submitted[submitted.length - 1] ?? [], progress.selected);
  const next: WordGroupsProgress = {
    ...progress,
    selected: selectionSent ? [] : progress.selected,
    guesses,
    outcomes: guesses.map((_, index) => outcomes[index] ?? "invalid"),
    found,
    mistakes: typeof params.mistakes === "number" ? params.mistakes : progress.mistakes,
  };
  if (verdict.correct) next.solved = true;
  return next;
}
